/**
 * Hook: useAffiliateImportJobs
 *
 * Centraliza chamadas RPC do histórico de importações de afiliados.
 * Toda comunicação com o banco passa por funções SECURITY DEFINER.
 *
 * Funções expostas:
 *   - listJobs (useQuery)   → list_affiliate_import_jobs
 *   - getJob   (useQuery)   → get_affiliate_import_job
 *   - deleteJob (mutation)  → delete_affiliate_import_job
 *
 * RELEASE: 2026-05-17 v1.0.0
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const log = createLogger('useAffiliateImportJobs');

// =============================================================================
// TIPOS
// =============================================================================

export interface AffiliateImportJobRow {
  id: string;
  name: string;
  status:
    | 'draft'
    | 'validating'
    | 'validated'
    | 'awaiting_approval'
    | 'enqueued'
    | 'processing'
    | 'completed'
    | 'completed_with_errors'
    | 'failed'
    | 'cancelled';
  program_id: string | null;
  program_name: string | null;
  total_rows: number;
  valid_count: number;
  invalid_count: number;
  duplicate_count: number;
  existing_count: number;
  processed_count: number;
  succeeded_count: number;
  failed_count: number;
  approved_count: number;
  force_approve: boolean;
  has_errors: boolean;
  source_file_path: string | null;
  source_file_name: string | null;
  created_at: string;
  finished_at: string | null;
  total_count: number;
}

interface ListParams {
  search?: string | null;
  statuses?: string[] | null;
  programId?: string | null;
  page: number;
  pageSize: number;
  enabled?: boolean;
}

// =============================================================================
// HOOK
// =============================================================================

export function useAffiliateImportJobs(params: ListParams) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;
  const qc = useQueryClient();

  const listJobs = useQuery({
    queryKey: [
      'affiliate-import-jobs',
      tenantId,
      params.search ?? '',
      params.statuses ?? [],
      params.programId ?? '',
      params.page,
      params.pageSize,
    ],
    enabled: !!tenantId && (params.enabled ?? true),
    // Auto-refresh enquanto houver jobs processando (a tela decide por rows)
    refetchInterval: 5000,
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('list_affiliate_import_jobs', {
        p_search: params.search?.trim() || null,
        p_statuses: params.statuses && params.statuses.length > 0 ? params.statuses : null,
        p_program_id: params.programId || null,
        p_page: params.page,
        p_page_size: params.pageSize,
      });
      if (error) {
        log.error('Falha ao listar importações de afiliados', { error: error.message });
        throw error;
      }
      return (data ?? []) as AffiliateImportJobRow[];
    },
  });

  const deleteJob = useMutation({
    mutationFn: async (jobId: string) => {
      const { error } = await (supabase as any).rpc('delete_affiliate_import_job', {
        p_job_id: jobId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['affiliate-import-jobs'] });
    },
  });

  // Cancela um job em andamento (drena mensagens PGMQ pendentes do lote).
  const cancelJob = useMutation({
    mutationFn: async (jobId: string) => {
      const { error } = await (supabase as any).rpc('cancel_affiliate_import_job', {
        p_job_id: jobId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['affiliate-import-jobs'] });
    },
  });

  return { listJobs, deleteJob, cancelJob };
}
